import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getData_Any2Column, deleteData } from "../service/APIService";
import img3 from "../images/frying-pan.gif";
import { Modal } from "react-bootstrap";
import NutritionComponent from "./NutritionComponent";
import secureLocalStorage from "react-secure-storage";
import { useSubscription } from "../context/SubscriptionContext";

const CalorieHistoryComponent = ({ showLatest = false, variant = "list" }) => {
  const { isSubscribed } = useSubscription();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [visibleRecipes, setVisibleRecipes] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [touchStartY, setTouchStartY] = useState(null);
  // Retrieve the logged-in user's email from localStorage
  const loggedInUserEmail = JSON.parse(secureLocalStorage.getItem("loggedInUser"))?.email || "guest";

  useEffect(() => {
    const fetchRecipeHistory = async () => {
      try {
        const data = await getData_Any2Column("email", loggedInUserEmail, "type", "recipe-ai-food-calorie");
        // If `showLatest` is true, limit to the latest 6 recipes
        const sortedRecipes = data.sort((a, b) => new Date(b.date) - new Date(a.date));
        setRecipes(showLatest ? sortedRecipes.slice(0, 6) : sortedRecipes);

        setError(null);
      } catch (err) {
        setError("Failed to fetch history. Please try again.");
        console.error("Error fetching history:", err);
      } finally {
        setLoading(false);
      }
    };

    if (loggedInUserEmail !== "guest") {
      fetchRecipeHistory();
    } else {
      setLoading(false);
    }
  }, [loggedInUserEmail, showLatest]);

  const handleShowModal = (recipe) => {
    setSelectedRecipe(recipe);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleLoadMore = () => {
    setVisibleRecipes((prev) => prev + 10); // Show 10 more records
  };

  const handleDeleteRecipe = async (recipeId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this record?");
    if (!confirmDelete) return;

    try {
      await deleteData(recipeId); // Call the delete API
      setRecipes((prevRecipes) => prevRecipes.filter((recipe) => recipe.id !== recipeId)); // Remove from UI
    } catch (err) {
      console.error("Error deleting record:", err);
      setError("Failed to delete. Please try again.");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear().toString().slice(-2);
      let hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12;
      return `${month}/${day}/${year} ${hours}:${minutes} ${ampm}`;
    } catch (e) {
      return dateString;
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const now = new Date();
      const isToday =
        date.getDate() === now.getDate() &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear();

      let hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, "0");
      const ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12;
      hours = hours ? hours : 12;
      const timeStr = `${hours}:${minutes} ${ampm}`;

      if (isToday) return timeStr;

      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      const isYesterday =
        date.getDate() === yesterday.getDate() &&
        date.getMonth() === yesterday.getMonth() &&
        date.getFullYear() === yesterday.getFullYear();

      if (isYesterday) return `Yesterday ${timeStr}`;

      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${months[date.getMonth()]} ${date.getDate()}, ${timeStr}`;
    } catch (e) {
      return dateString;
    }
  };

  const formatCalVal = (cal) => {
    if (!cal) return "+ 0 Calories";
    const str = String(cal).replace(/calories/gi, "").replace(/kcal/gi, "").trim();
    return `+ ${str} Calories`;
  };

  const formatMacroVal = (val, fallback = "") => {
    if (!val) return fallback;
    const str = String(val).trim();
    return str.toLowerCase().endsWith("g") ? str : `${str}g`;
  };

  const filteredRecipes = recipes.filter((recipe) => (recipe.title || "").toLowerCase().includes(searchQuery.toLowerCase()));

  const fallbackDemoScans = [
    {
      id: "demo-steak",
      title: "Steak",
      subtitle: "Timeless taste",
      calories: "450",
      carbs: "10.5%",
      protein: "42g",
      sugar: "2.5%",
      fat: "50.5%",
      date: new Date().toISOString(),
      image: "https://images.unsplash.com/photo-1544025162-d76694265947?w=500&auto=format&fit=crop&q=80",
    },
    {
      id: "demo-padang",
      title: "Padang rice",
      subtitle: "Timeless Minang Taste",
      calories: "580",
      carbs: "80.5%",
      protein: "24g",
      sugar: "60.9%",
      fat: "70.5%",
      date: new Date().toISOString(),
      image: "https://images.unsplash.com/photo-1596797038530-2c107229654b?w=500&auto=format&fit=crop&q=80",
    },
    {
      id: "demo-spagetti",
      title: "Spagetti",
      subtitle: "Timeless taste",
      calories: "420",
      carbs: "65.5%",
      protein: "14g",
      sugar: "20.1%",
      fat: "70.5%",
      date: new Date().toISOString(),
      image: "https://images.unsplash.com/photo-1551183053-bf91a1d81141?w=500&auto=format&fit=crop&q=80",
    },
  ];

  // In carousel mode, if recipes has fewer than 3 items, augment with demo scans
  const carouselItems =
    recipes.length >= 3
      ? recipes.slice(0, 5)
      : recipes.length > 0
      ? [...recipes, ...fallbackDemoScans.slice(recipes.length)]
      : fallbackDemoScans;

  if (variant === "carousel") {
    return (
      <div className="last-scans-section mt-4 mb-2">
        {/* Header row: Last scans + See all */}
        <div className="d-flex justify-content-between align-items-center mb-3 px-1">
          <h5 className="fw-bolder text-dark mb-0" style={{ fontSize: "1.18rem", letterSpacing: "-0.4px" }}>
            Last scans
          </h5>
          <Link
            to="/history?tab=calories"
            className="text-decoration-none fw-semibold text-secondary small d-flex align-items-center gap-1"
            style={{ fontSize: "13px" }}
          >
            <span>See all</span>
          </Link>
        </div>

        {/* Carousel container matching screenshot */}
        <div className="last-scans-carousel">
          {carouselItems.map((recipe, index) => {
            const rawTitle = recipe.title || "Dish";
            const cleanTitle = rawTitle.replace(/"/g, "").replace(/\*\*/g, "").trim();
            const carbs = recipe.carbs ? (String(recipe.carbs).includes("%") ? recipe.carbs : formatMacroVal(recipe.carbs, "42g")) : "80.5%";
            const sugar = recipe.sugar ? (String(recipe.sugar).includes("%") ? recipe.sugar : formatMacroVal(recipe.sugar, "18g")) : (recipe.protein ? formatMacroVal(recipe.protein, "24g") : "60.9%");
            const fats = recipe.fat ? (String(recipe.fat).includes("%") ? recipe.fat : formatMacroVal(recipe.fat, "22g")) : "70.5%";
            const isCenterCard = index === 1 || (carouselItems.length === 1 && index === 0);

            return (
              <div
                key={recipe.id || index}
                className={`last-scan-card ${isCenterCard ? "center-featured" : ""}`}
                onClick={() => handleShowModal(recipe)}
                role="button"
                tabIndex={0}
              >
                {/* Circular plate photo with soft drop shadow */}
                <div className="last-scan-plate-wrap">
                  {recipe.image ? (
                    <img src={recipe.image} alt={cleanTitle} className="last-scan-plate-img" />
                  ) : (
                    <div className="last-scan-plate-placeholder">
                      <i className="fas fa-utensils"></i>
                    </div>
                  )}
                </div>

                {/* Card Body */}
                <div className="last-scan-card-body text-center">
                  <h6 className="last-scan-title mb-0" title={cleanTitle}>
                    {cleanTitle}
                  </h6>
                  <p className="last-scan-subtitle mb-2">
                    {recipe.subtitle || (recipe.date ? formatTimeAgo(recipe.date) : "Timeless taste")}
                  </p>

                  {/* 3 Micro Macro Icons & Values Row */}
                  <div className="last-scan-macro-icons-row">
                    <div className="macro-micro-item carbs" title="Carbs">
                      <span className="micro-glyph">🌾</span>
                      <span className="micro-val">{carbs}</span>
                      <span className="micro-lbl">Carbs</span>
                    </div>
                    <div className="macro-micro-item sugar" title="Sugar / Protein">
                      <span className="micro-glyph">🍬</span>
                      <span className="micro-val">{sugar}</span>
                      <span className="micro-lbl">Sugar</span>
                    </div>
                    <div className="macro-micro-item fats" title="Fats">
                      <span className="micro-glyph">💧</span>
                      <span className="micro-val">{fats}</span>
                      <span className="micro-lbl">Fats</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Modal when clicked */}
        {selectedRecipe && (
          <Modal
            show={showModal}
            onHide={handleCloseModal}
            onExited={() => setSelectedRecipe(null)}
            className="bottom-sheet-modal"
            dialogClassName="bottom-sheet-dialog"
          >
            <Modal.Body className="p-0">
              <div
                onTouchStart={(e) => setTouchStartY(e.touches[0].clientY)}
                onTouchEnd={(e) => {
                  if (touchStartY !== null && e.changedTouches[0].clientY - touchStartY > 80) {
                    handleCloseModal();
                  }
                  setTouchStartY(null);
                }}
              >
                {/* Modal Hero Image */}
                <div className="recipe-modal-hero-wrap">
                  {selectedRecipe.image ? (
                    <img
                      src={selectedRecipe.image}
                      alt={selectedRecipe.title}
                      className="recipe-modal-hero-img"
                    />
                  ) : (
                    <div className="recipe-modal-hero-placeholder" style={{ background: "linear-gradient(135deg, #7c2d12 0%, #431407 100%)" }}>
                      <i className="fas fa-fire text-white opacity-50"></i>
                    </div>
                  )}
                  <button
                    type="button"
                    className="recipe-modal-close-btn"
                    onClick={handleCloseModal}
                    aria-label="Close"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                  {selectedRecipe.calories && (
                    <span className="recipe-modal-cuisine-badge" style={{ background: "rgba(255, 247, 237, 0.95)", color: "#ea580c" }}>
                      🔥 {String(selectedRecipe.calories).includes("kcal") ? selectedRecipe.calories : `${selectedRecipe.calories} kcal`}
                    </span>
                  )}
                </div>

                {/* Content below image */}
                <div className="px-3 pt-3 pb-1">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="text-muted d-flex align-items-center gap-1" style={{ fontSize: "0.74rem" }}>
                      <i className="fi fi-rr-clock"></i> {formatDate(selectedRecipe.date)}
                    </span>
                    <span className="badge rounded-pill" style={{ background: "#fff7ed", color: "#ea580c", border: "1px solid #ffedd5", fontSize: "11px", fontWeight: "600" }}>
                      🔥 Nutrition Scan
                    </span>
                  </div>

                  <h4 className="fw-bolder text-dark mb-3" style={{ fontSize: "1.25rem", lineHeight: "1.3", letterSpacing: "-0.3px" }}>
                    {selectedRecipe.title && selectedRecipe.title.replace(/"/g, "").trim()}
                  </h4>

                  {/* Redesigned NutritionComponent */}
                  <NutritionComponent nutritionInfo={selectedRecipe} />
                </div>
              </div>
            </Modal.Body>

            <Modal.Footer className="border-0 pt-0 px-3 pb-3">
              <button
                className="btn w-100 py-3 rounded-pill fw-bold text-white shadow-none"
                style={{
                  background: "#0f1f14",
                  border: "1px solid rgba(34, 197, 94, 0.35)",
                  fontSize: "0.95rem",
                  boxShadow: "0 4px 14px rgba(15, 31, 20, 0.35)"
                }}
                onClick={handleCloseModal}
              >
                Done
              </button>
            </Modal.Footer>
          </Modal>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mt-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <h6 className="fw-bold text-dark mb-0">{showLatest ? "Recent Calorie Checks" : "Calorie History"}</h6>
          {showLatest && (
            <span className="badge rounded-pill bg-success bg-opacity-10 text-success border border-success border-opacity-25" style={{ fontSize: "10px" }}>
              Latest 6
            </span>
          )}
        </div>
        {showLatest && (
          <Link to="/history?tab=calories" className="text-decoration-none fw-semibold text-success small d-flex align-items-center gap-1" style={{ fontSize: "12px" }}>
            <span>View All</span>
            <i className="fi fi-rr-arrow-right" style={{ fontSize: "10px" }}></i>
          </Link>
        )}
      </div>

      {!showLatest && (
        <div className="mb-3">
          <input
            type="text"
            className="form-control"
            placeholder="Search meal records..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              borderRadius: "9999px",
              padding: "10px 18px",
              border: "1.5px solid #e2e8f0",
              fontSize: "0.88rem"
            }}
          />
        </div>
      )}

      {loading ? (
        <div className="text-center py-4">
          <div className="d-inline-flex align-items-center justify-content-center p-3 rounded-circle" style={{ background: "rgba(34, 197, 94, 0.1)" }}>
            <span style={{ fontSize: "2.4rem", animation: "potSizzle 1.2s ease-in-out infinite alternate", display: "inline-block" }}>🥗</span>
          </div>
          <p className="text-secondary small mt-2 mb-0 fw-medium">Loading history...</p>
        </div>
      ) : error ? (
        <p style={{ color: "red" }}>{error}</p>
      ) : recipes.length === 0 ? (
        <p className="text-muted text-center py-4">No calorie check records found.</p>
      ) : (
        <div className="recipe-history-list">
          <div className="d-flex flex-column gap-2 mb-2">
            {filteredRecipes.slice(0, showLatest ? 6 : visibleRecipes).map((recipe) => (
              <div
                key={recipe.id}
                className="recent-food-horizontal-card calorie-card"
                onClick={() => handleShowModal(recipe)}
                role="button"
                tabIndex={0}
              >
                {/* Left Media (Thumbnail 64x64 compact mobile-first) */}
                <div
                  className="food-card-thumb"
                  style={{
                    width: "64px",
                    height: "64px",
                    minWidth: "64px",
                    maxWidth: "64px",
                    borderRadius: "14px",
                    overflow: "hidden",
                    flexShrink: 0
                  }}
                >
                  {recipe.image ? (
                    <img
                      src={recipe.image}
                      alt={recipe.title}
                      className="food-card-img"
                      style={{
                        width: "100%",
                        height: "100%",
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "cover",
                        display: "block"
                      }}
                      loading="lazy"
                    />
                  ) : (
                    <div className="food-card-placeholder calorie">
                      <i className="fas fa-fire"></i>
                    </div>
                  )}
                </div>

                {/* Center Details */}
                <div className="food-card-info">
                  {/* Top line: Name + Time */}
                  <div className="food-card-header">
                    <h6 className="food-card-name" title={recipe.title ? recipe.title.replace(/"/g, "").trim() : "Food Check"}>
                      {recipe.title ? recipe.title.replace(/"/g, "").trim() : "Food Check"}
                    </h6>
                    <span className="food-card-time">
                      {formatTimeAgo(recipe.date)}
                    </span>
                  </div>

                  {/* Second line: Calorie count matching reference "+ 45 Calories" */}
                  <div className="food-card-subline">
                    <span className="food-calories-highlight">
                      {formatCalVal(recipe.calories)}
                    </span>
                  </div>

                  {/* Third line: Nutrients breakdown matching reference mockup (Protein, Carbs, Fat) */}
                  <div className="food-macro-row">
                    <span className="food-macro-stat" title="Protein">
                      <span role="img" aria-label="protein">🍗</span>
                      <span>{formatMacroVal(recipe.protein, "24g")}</span>
                    </span>
                    <span className="food-macro-stat" title="Carbs">
                      <span role="img" aria-label="carbs">🌾</span>
                      <span>{formatMacroVal(recipe.carbs, "42g")}</span>
                    </span>
                    <span className="food-macro-stat" title="Fat">
                      <span role="img" aria-label="fat">🥑</span>
                      <span>{formatMacroVal(recipe.fat, "14g")}</span>
                    </span>
                  </div>
                </div>

                {/* Right Action Chevron or Delete */}
                <div className="food-card-actions">
                  {isSubscribed && !showLatest ? (
                    <button
                      type="button"
                      className="food-card-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteRecipe(recipe.id);
                      }}
                      title="Delete record"
                      aria-label="Delete record"
                    >
                      <i className="fi fi-rr-trash"></i>
                    </button>
                  ) : (
                    <div className="food-card-chevron">
                      <i className="fi fi-rr-angle-small-right"></i>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {showLatest ? (
            <div align="center" className="mt-3 mb-1">
              <Link
                to="/history?tab=calories"
                className="btn py-2 px-4 rounded-pill fw-bold shadow-xs d-inline-flex align-items-center gap-2 text-decoration-none view-full-history-btn"
                style={{
                  fontSize: "0.82rem"
                }}
              >
                <span>View Full Calorie & Scan History</span>
                <i className="fi fi-rr-arrow-right" style={{ fontSize: "0.85rem" }}></i>
              </Link>
            </div>
          ) : (
            visibleRecipes < filteredRecipes.length && (
              <div align="center" className="mt-4 mb-2">
                <button
                  type="button"
                  className="btn py-2 px-5 rounded-pill fw-bold shadow-sm d-flex align-items-center gap-2"
                  style={{
                    backgroundColor: "#0f1f14",
                    color: "#ffffff",
                    border: "1px solid rgba(34, 197, 94, 0.35)",
                    boxShadow: "0 4px 14px rgba(15, 31, 20, 0.35)"
                  }}
                  onClick={handleLoadMore}
                >
                  <span>Load More</span>
                  <i className="fi fi-rr-angle-small-down" style={{ fontSize: "1.2rem" }}></i>
                </button>
              </div>
            )
          )}
        </div>
      )}

      {selectedRecipe && (
        <Modal
          show={showModal}
          onHide={handleCloseModal}
          onExited={() => setSelectedRecipe(null)}
          className="bottom-sheet-modal"
          dialogClassName="bottom-sheet-dialog"
        >
          <Modal.Body className="p-0">
            <div
              onTouchStart={(e) => setTouchStartY(e.touches[0].clientY)}
              onTouchEnd={(e) => {
                if (touchStartY !== null && e.changedTouches[0].clientY - touchStartY > 80) {
                  handleCloseModal();
                }
                setTouchStartY(null);
              }}
            >
              {/* Full-width Hero Image */}
              <div className="recipe-modal-hero">
                {selectedRecipe.image ? (
                  <img
                    src={selectedRecipe.image}
                    alt={selectedRecipe.title}
                    className="recipe-modal-hero-img"
                  />
                ) : (
                  <div className="recipe-modal-hero-placeholder" style={{ background: "linear-gradient(135deg, #7c2d12 0%, #431407 100%)" }}>
                    <i className="fas fa-fire text-white opacity-50"></i>
                  </div>
                )}
                {/* Close button overlaid on image */}
                <button
                  type="button"
                  className="recipe-modal-close-btn"
                  onClick={handleCloseModal}
                  aria-label="Close"
                >
                  <i className="fas fa-times"></i>
                </button>
                {/* Floating Calorie Badge */}
                {selectedRecipe.calories && (
                  <span className="recipe-modal-cuisine-badge" style={{ background: "rgba(255, 247, 237, 0.95)", color: "#ea580c" }}>
                    🔥 {String(selectedRecipe.calories).includes("kcal") ? selectedRecipe.calories : `${selectedRecipe.calories} kcal`}
                  </span>
                )}
              </div>

              {/* Content below image */}
              <div className="px-3 pt-3 pb-1">
                {/* Meta Header Row */}
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="text-muted d-flex align-items-center gap-1" style={{ fontSize: "0.74rem" }}>
                    <i className="fi fi-rr-clock"></i> {formatDate(selectedRecipe.date)}
                  </span>
                  <span className="badge rounded-pill" style={{ background: "#fff7ed", color: "#ea580c", border: "1px solid #ffedd5", fontSize: "11px", fontWeight: "600" }}>
                    🔥 Nutrition Scan
                  </span>
                </div>

                {/* Title */}
                <h4 className="fw-bolder text-dark mb-3" style={{ fontSize: "1.25rem", lineHeight: "1.3", letterSpacing: "-0.3px" }}>
                  {selectedRecipe.title && selectedRecipe.title.replace(/"/g, "").trim()}
                </h4>

                {/* Graphical Animated Nutrition Component */}
                <NutritionComponent nutritionInfo={selectedRecipe} />
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer className="border-0 pt-0 px-3 pb-3">
            <button
              className="btn w-100 py-3 rounded-pill fw-bold text-white shadow-none"
              style={{
                background: "#0f1f14",
                border: "1px solid rgba(34, 197, 94, 0.35)",
                fontSize: "0.95rem",
                boxShadow: "0 4px 14px rgba(15, 31, 20, 0.35)"
              }}
              onClick={handleCloseModal}
            >
              Done
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default CalorieHistoryComponent;
